import { useCallback, useContext, useEffect, useRef } from 'react';
import useMsal from '../../auth/useMsal';
import getContext from '../../context/AppSettingsContext';
import { SWApiMessageProps } from './SWApiMessageProps';
import queryParamsUtils from '../../utils/queryParamsUtils';
import { useConnectionStore } from 'src/context/ConnectionContext';
import useApiEndpoints from 'src/context/useApiEndpoints';
import { useRotationResponseHandler } from '../responseHandlers/useRotationResponseHandler';
import { useBunkerRefreshResponseHandler } from '../responseHandlers/useBunkerRefreshResponseHandler';
import { requestThrottleManager } from 'src/utils/requestThrottleManager';
import { clearServiceWorkerCache, updateAndActivateServiceWorker } from './serviceWorkerRefresh';

export const useSWService = () => {
    const appSettings = useContext(getContext());
    const { interceptSWRequest } = useMsal();
    const { hasAuthorizedAppToken, getAppAccessToken } = queryParamsUtils();
    const { isOfflineSimulated } = useConnectionStore();
    const { apiEndpoints } = useApiEndpoints();
    const { onGetImosRotation } = useRotationResponseHandler();
    const { onGetBunkerings, onGetFuelRobData, onGetBunkerLossData } = useBunkerRefreshResponseHandler();

    // The registered listener and the startup cache sweep delegate through these refs rather than
    // closing over their functions directly, so a single long-lived listener always runs the current
    // render's handlers instead of captured ones holding stale context setters or a stale releaseId.
    const handleSWResponseRef = useRef<(event: MessageEvent) => void>(() => undefined);
    const removeOldCacheItemsRef = useRef<(registration: ServiceWorkerRegistration) => void>(() => undefined);

    const setupServiceWorker = useCallback(() => {
        if (!('serviceWorker' in navigator)) return undefined;

        navigator.serviceWorker.ready.then((registration) => {
            setTimeout(() => removeOldCacheItemsRef.current(registration), 25);
        });

        const messageListener = (event: MessageEvent) => handleSWResponseRef.current(event);
        navigator.serviceWorker.addEventListener('message', messageListener);

        return () => navigator.serviceWorker.removeEventListener('message', messageListener);
    }, []);

    const refreshRequestsContaining = async (endpoints: string[], queryParams?: string, additionalData?: any) => {
        if ('serviceWorker' in navigator && navigator.onLine && !(await isOfflineSimulated())) {
            const registration = await navigator.serviceWorker.ready;

            for (const endpoint of endpoints) {
                const requestKey = `refreshRequestsContaining:${endpoint}:${queryParams || ''}`;

                try {
                    await requestThrottleManager.executeBypassCooldown(requestKey, async () => {
                        const props: SWApiMessageProps = {
                            type: 'refresh_containing',
                            forceRefresh: true,
                            queryParams: queryParams,
                            additionalData,
                        };

                        await postApiMessage(registration, endpoint, props);
                        return true;
                    });
                } catch (error) {
                    console.warn(`refreshRequestsContaining: Refresh failed for ${endpoint}, evicting stale cache`, error);
                    await removeRequestsContaining([endpoint], queryParams);
                }
            }
        }
    };

    const removeRequestsContaining = async (endpoints: string[], queryParams?: string) => {
        if ('serviceWorker' in navigator && navigator.onLine && !(await isOfflineSimulated())) {
            const registration = await navigator.serviceWorker.ready;

            endpoints.forEach((endpoint) => {
                const props: SWApiMessageProps = {
                    type: 'remove_containing',
                    queryParams: queryParams,
                };

                postApiMessage(registration, endpoint, props);
            });
        }
    };

    const addAppAccessToken = (queryParams: string, appAccessToken: string): string => {
        return queryParams + '&appAccessToken=' + appAccessToken;
    };

    const postApiMessage = async (registration: ServiceWorkerRegistration, endpoint: string, props: SWApiMessageProps) => {
        const body = props.postBody ? JSON.stringify(props.postBody) : undefined;

        if (hasAuthorizedAppToken()) {
            const appAccessToken = getAppAccessToken();
            const queryParams = props.queryParams ? addAppAccessToken(props.queryParams, appAccessToken) : undefined;
            const queryParamsList = props.queryParamsList ? props.queryParamsList.map((o) => addAppAccessToken(o, appAccessToken)) : undefined;
            registration.active.postMessage({
                type: props.type,
                baseUrl: appSettings.api,
                endpoint,
                forceRefresh: props.forceRefresh,
                queryParams: queryParams,
                queryParamsList: queryParamsList,
                additionalData: props.additionalData,
                body,
            });
        } else {
            const accessToken = await interceptSWRequest();
            if (accessToken) {
                registration.active.postMessage({
                    type: props.type,
                    baseUrl: appSettings.api,
                    endpoint,
                    forceRefresh: props.forceRefresh,
                    queryParams: props.queryParams,
                    queryParamsList: props.queryParamsList,
                    additionalData: props.additionalData,
                    body,
                    token: accessToken,
                });
            }
        }
    };

    const callApiEndpoint = async (endpoint: string, queryParams?: string) => {
        if ('serviceWorker' in navigator) {
            const registration = await navigator.serviceWorker.ready;

            queryParams += '&releaseId=' + appSettings.releaseId;
            const props: SWApiMessageProps = {
                type: 'api_call',
                forceRefresh: false,
                queryParams: queryParams,
            };

            await postApiMessage(registration, endpoint, props);
        }
    };

    const clearCache = (): Promise<void> => clearServiceWorkerCache();

    const refreshServiceWorker = (): Promise<void> => updateAndActivateServiceWorker();

    const removeOldCacheItems = (registration: ServiceWorkerRegistration) => {
        const releaseId = appSettings.releaseId;
        registration.active.postMessage({
            type: 'remove_old_items',
            releaseId,
        });
    };

    const handleSWResponse = (event: MessageEvent) => {
        const responses = event.data?.responses;
        if (!responses) return;

        const arr = Array.isArray(responses);
        if (arr && responses.length > 0) {
            responses.forEach((response) => {
                if (response.url && response.responseData) {
                    handleRefreshUrl(response.url, response.responseData, event.data?.additionalData);
                }
            });
        } else {
            if (responses.url && responses.responseData) {
                handleRefreshUrl(responses.url, responses.responseData, event.data?.additionalData);
            }
        }
    };

    const handleRefreshUrl = (url: string, responseData: any, additionalData: any) => {
        const pathName = new URL(url).pathname;
        const endpoint = pathName.substring(pathName.lastIndexOf('/') + 1);

        if (endpoint === apiEndpoints['getRotation'].url) {
            onGetImosRotation(responseData, additionalData, true);
        } else if (endpoint === apiEndpoints['getBunkerings'].url) {
            onGetBunkerings(responseData);
        } else if (endpoint === apiEndpoints['getFuelRobData'].url) {
            onGetFuelRobData(responseData);
        } else if (endpoint === apiEndpoints['getBunkerLossData'].url) {
            onGetBunkerLossData(responseData);
        }
    };

    useEffect(() => {
        handleSWResponseRef.current = handleSWResponse;
        removeOldCacheItemsRef.current = removeOldCacheItems;
    });

    return {
        setupServiceWorker,
        callApiEndpoint,
        clearCache,
        refreshServiceWorker,
        refreshRequestsContaining,
        removeRequestsContaining,
    };
};
